import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { apiClient } from "../../../api/apiClient";
import MedicoSidebar from "../../../components/sidebars/medico/MedicoSidebar";
import NotificacionesBell from "../../../components/notificaciones/NotificacionesBell";
import { NotificacionesProvider } from "../../../components/notificaciones/NotificacionesContext";
import Disponibilidad from "./Disponibilidad";
import MisTurnos from "./MisTurnos";
import Servicios from "./Servicios";
import {
    getEntityId,
    normalizeTurno,
    normalizeSede,
    normalizeServicio,
    normalizeDisponibilidad,
    resolveMedico,
} from "./medicoHelpers";
import {
    actualizarTurno,
    aceptarCambioFecha,
    rechazarCambioFecha,
    proponerCambioFecha,
    agregarServicioAMedico,
} from "../../../api/apiResources";
import "./MedicoLayout.css";

const MedicoLayout = ({ user, onLogout }) => {
    const { page } = useParams();
    const [activePage, setActivePage] = useState(page || "turnos");
    const [turnos, setTurnos] = useState([]);
    const [sedes, setSedes] = useState([]);
    const [servicios, setServicios] = useState([]);
    const [todosServicios, setTodosServicios] = useState([]);
    const [disponibilidades, setDisponibilidades] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [medicoActual, setMedicoActual] = useState(null);

    const loadData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [medicosRes, turnosRes, sedesRes, todosServiciosRes] = await Promise.all([
                apiClient.get("/medicos"),
                apiClient.get("/turnos/buscar?limit=500"),
                apiClient.get("/sedes"),
                apiClient.get("/servicios"),
            ]);

            const medicosData = Array.isArray(medicosRes?.data)
                ? medicosRes.data
                : (Array.isArray(medicosRes) ? medicosRes : []);

            const turnosData = Array.isArray(turnosRes?.data)
                ? turnosRes.data
                : (Array.isArray(turnosRes) ? turnosRes : []);

            const sedesData = Array.isArray(sedesRes?.data)
                ? sedesRes.data
                : (Array.isArray(sedesRes) ? sedesRes : []);

            const todosServiciosData = Array.isArray(todosServiciosRes?.data)
                ? todosServiciosRes.data
                : (Array.isArray(todosServiciosRes) ? todosServiciosRes : []);

            const matchedMedico = resolveMedico(medicosData, user);
            setMedicoActual(matchedMedico);

            if (!matchedMedico) {
                console.warn("No se encontró un médico asociado al usuario actual:", user);
                setTurnos([]);
                setSedes(sedesData.map(normalizeSede));
                setServicios([]);
                setTodosServicios(todosServiciosData.map(normalizeServicio));
                setDisponibilidades([]);
                return;
            }

            const matchedMedicoId = getEntityId(matchedMedico);

            let serviciosData = [];

            try {
                const serviciosRes = await apiClient.get(
                    `/medicos/${matchedMedicoId}/servicios`
                );

                console.log("Respuesta del backend:", serviciosRes.data);

                const raw = serviciosRes?.data;

                serviciosData = Array.isArray(raw) ? raw : [];
            } catch (servErr) {
                console.warn("No se pudieron cargar los servicios:", servErr);
                serviciosData = [];
            }

            let disponibilidadesData = [];
            try {
                const dispRes = await apiClient.get(`/medicos/${matchedMedicoId}/disponibilidades`);
                disponibilidadesData = Array.isArray(dispRes?.data) ? dispRes.data : (Array.isArray(dispRes) ? dispRes : []);
            } catch (dispErr) {
                console.warn("No se pudieron cargar disponibilidades:", dispErr);
                disponibilidadesData = Array.isArray(matchedMedico.disponibilidades) ? matchedMedico.disponibilidades : [];
            }

            const filteredTurnos = turnosData
                .filter((turno) => {
                    if (!turno) return false;
                    return getEntityId(turno.id_medico) === matchedMedicoId;
                })
                .map(normalizeTurno);

            setTurnos(filteredTurnos);
            setSedes(sedesData.map(normalizeSede));
            setServicios(serviciosData.map(normalizeServicio));
            setTodosServicios(todosServiciosData.map(normalizeServicio));
            setDisponibilidades(disponibilidadesData.map(normalizeDisponibilidad));
        } catch (err) {
            console.error("Error cargando datos del médico:", err);
            setError("No se pudieron cargar los datos. Por favor, intente nuevamente más tarde.");
        } finally {
            setLoading(false);
        }
    }, [user]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    useEffect(() => {
        if (page) setActivePage(page);
    }, [page]);

    const handleActualizarTurno = async (turnoId, cambios) => {
        await actualizarTurno(turnoId, cambios);
        loadData();
    };

    const handleProponerCambio = async (turno, nuevaFecha) => {
        await proponerCambioFecha(turno.id, nuevaFecha, getEntityId(medicoActual), null);
        loadData();
    };

    const handleAceptarCambio = async (turno) => {
        await aceptarCambioFecha(turno.id, getEntityId(medicoActual), null);
        loadData();
    };

    const handleRechazarCambio = async (turno) => {
        await rechazarCambioFecha(turno.id, getEntityId(medicoActual), null);
        loadData();
    };

    const handleAgregarServicio = async (servicioId) => {
        await agregarServicioAMedico(
            getEntityId(medicoActual),
            servicioId
        );

        await loadData();
    };

    const serviciosDisponibles = todosServicios.filter(
        (servicio) =>
            !servicios.some((s) => s.id === servicio.id)
    );

    return (
        <NotificacionesProvider user={user}>
            <div className="medico-layout">
                <NotificacionesBell />
                <MedicoSidebar currentPage={activePage} onNavigate={setActivePage} user={user} onLogout={onLogout} />
                <main id="medico-main-content" className="medico-layout__main" tabIndex={-1} aria-label="Contenido principal">
                    {activePage === "turnos" && (
                        <MisTurnos
                            turnos={turnos}
                            loading={loading}
                            error={error}
                            medico={medicoActual}
                            onActualizarTurno={handleActualizarTurno}
                            onProponerCambio={handleProponerCambio}
                            onAceptarCambio={handleAceptarCambio}
                            onRechazarCambio={handleRechazarCambio}
                            onTurnoModificado={loadData}
                        />
                    )}
                    {activePage === "disponibilidad" && (
                        <Disponibilidad
                            disponibilidades={disponibilidades}
                            sedes={sedes}
                            loading={loading}
                            error={error}
                            medico={medicoActual}
                            onDisponibilidadModificada={loadData}
                        />
                    )}
                    {activePage === "servicios" && (
                        <Servicios
                            servicios={servicios}
                            serviciosDisponibles={serviciosDisponibles}
                            loading={loading}
                            error={error}
                            onAgregarServicio={handleAgregarServicio}
                        />
                    )}
                </main>
            </div>
        </NotificacionesProvider>
    );
};

export default MedicoLayout;